import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from 'notistack';
import VendedorSelect from '../../components/VendedorSelect';
import FiltroSucursalVentaSelect from '../../components/shared/FiltroSucursalVentaSelect';
import { useSucursalesVentaFiltro } from '../../hooks/useSucursalesVentaFiltro';
import comisionesService, {
  DocumentoLiquidableDto,
  DocumentosLiquidablesResult,
  getComisionErrorMessage,
  importeBaseDocumento,
  labelImporteBase,
  LiquidarComisionResult,
} from '../../services/comisionesService';
import { formatCurrency } from '../../utils/formatters';
import { formatFechaComision as formatFechaDisplay } from './liquidacionComisionesFormat';
import Can from '../../components/Can';

const LISTADO_LIQUIDACIONES = '/ventas/liquidacion-comisiones';

const parseImporteConcepto = (raw: string): number => {
  const normalized = raw.trim().replace(',', '.');
  if (!normalized) return 0;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
};

const LiquidacionComisiones: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { filtroNroSuc, setFiltroNroSuc, sucursales, loadingSucursales } = useSucursalesVentaFiltro();

  const [nroVendedor, setNroVendedor] = useState<number | null>(null);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [fechaEmision, setFechaEmision] = useState(comisionesService.todayIsoDate());

  const [preview, setPreview] = useState<DocumentosLiquidablesResult | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [liquidando, setLiquidando] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successResult, setSuccessResult] = useState<LiquidarComisionResult | null>(null);

  const [concCom1, setConcCom1] = useState('');
  const [concComImp1, setConcComImp1] = useState('');
  const [concCom2, setConcCom2] = useState('');
  const [concComImp2, setConcComImp2] = useState('');

  const rows = preview?.documentos ?? [];
  const comisionBase = preview?.comisionBase;

  const impConcepto1 = parseImporteConcepto(concComImp1);
  const impConcepto2 = parseImporteConcepto(concComImp2);
  const totalConceptos = impConcepto1 + impConcepto2;

  const totalDocs = useMemo(() => {
    return rows
      .filter((r) => selectedIds.has(r.nroFormulario))
      .reduce((acc, r) => acc + (Number(r.impComision) || 0), 0);
  }, [rows, selectedIds]);

  const totalComision = totalDocs + totalConceptos;

  const tieneProveedor = (preview?.nroProveedor ?? 0) > 0;
  const tieneCuenta = (preview?.nroCuentaLiquidacion ?? 0) > 0;
  const configLiquidacionOk = tieneProveedor && tieneCuenta;

  const toggleRow = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    if (!checked) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(rows.map((r) => r.nroFormulario).filter((id) => id > 0)));
  };

  const handleBuscar = async () => {
    if (!nroVendedor || nroVendedor <= 0) {
      enqueueSnackbar('Seleccioná un vendedor', { variant: 'warning' });
      return;
    }
    if (!fechaDesde || !fechaHasta) {
      enqueueSnackbar('Indicá el rango de fechas', { variant: 'warning' });
      return;
    }
    if (fechaDesde > fechaHasta) {
      enqueueSnackbar('La fecha Desde no puede ser mayor que Hasta', { variant: 'warning' });
      return;
    }

    try {
      setLoading(true);
      const result = await comisionesService.getDocumentosLiquidables({
        nroVendedor,
        desde: fechaDesde,
        hasta: fechaHasta,
        nroSuc: filtroNroSuc != null && filtroNroSuc > 0 ? filtroNroSuc : null,
      });
      const valid = (result.documentos ?? []).filter((d) => Number(d.nroFormulario) > 0);
      setPreview({ ...result, documentos: valid });
      setSelectedIds(new Set(valid.map((d) => d.nroFormulario)));
      if (valid.length === 0) {
        enqueueSnackbar('No hay comprobantes liquidables para el filtro indicado', {
          variant: 'info',
        });
      }
    } catch (error) {
      setPreview(null);
      setSelectedIds(new Set());
      enqueueSnackbar(getComisionErrorMessage(error, 'Error al buscar documentos liquidables'), {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const validarAntesDeLiquidar = (): boolean => {
    if (!nroVendedor || nroVendedor <= 0) {
      enqueueSnackbar('Seleccioná un vendedor', { variant: 'warning' });
      return false;
    }
    if (!filtroNroSuc || filtroNroSuc <= 0) {
      enqueueSnackbar(
        'Seleccioná la sucursal del comprobante X a generar (no puede ser “Todas”)',
        { variant: 'warning' },
      );
      return false;
    }
    if (!fechaEmision) {
      enqueueSnackbar('Indicá la fecha de emisión de la liquidación', { variant: 'warning' });
      return false;
    }
    if (selectedIds.size === 0) {
      enqueueSnackbar('Seleccioná al menos un comprobante', { variant: 'warning' });
      return false;
    }
    if (!preview) {
      enqueueSnackbar('Buscá los comprobantes liquidables antes de confirmar', { variant: 'warning' });
      return false;
    }
    if (!tieneProveedor) {
      enqueueSnackbar(
        'El vendedor no tiene proveedor asociado. Configuralo en Ventas → Vendedores (tab Comisiones).',
        { variant: 'warning' },
      );
      return false;
    }
    if (!tieneCuenta) {
      enqueueSnackbar(
        'El vendedor no tiene cuenta contable de comisión. Configurala en Ventas → Vendedores (tab Comisiones).',
        { variant: 'warning' },
      );
      return false;
    }
    if (impConcepto1 !== 0 && !concCom1.trim()) {
      enqueueSnackbar('Indicá la descripción del concepto adicional 1', { variant: 'warning' });
      return false;
    }
    if (impConcepto2 !== 0 && !concCom2.trim()) {
      enqueueSnackbar('Indicá la descripción del concepto adicional 2', { variant: 'warning' });
      return false;
    }
    return true;
  };

  const handleAbrirConfirmacion = () => {
    if (!validarAntesDeLiquidar()) return;
    setConfirmOpen(true);
  };

  const handleConfirmar = async () => {
    if (!validarAntesDeLiquidar()) {
      setConfirmOpen(false);
      return;
    }
    if (!nroVendedor || !filtroNroSuc) return;

    try {
      setLiquidando(true);
      setConfirmOpen(false);
      const result = await comisionesService.liquidar({
        nroVendedor,
        fechaEmision,
        nroSuc: filtroNroSuc,
        nroFormularios: Array.from(selectedIds),
        concCom1: concCom1.trim(),
        concComImp1: impConcepto1,
        concCom2: concCom2.trim(),
        concComImp2: impConcepto2,
      });

      if (!result.exitoso) {
        enqueueSnackbar(result.mensaje?.trim() || 'No se pudo liquidar la comisión', {
          variant: 'error',
        });
        return;
      }

      setPreview((prev) => {
        if (!prev) return null;
        const restantes = prev.documentos.filter((r) => !selectedIds.has(r.nroFormulario));
        return {
          ...prev,
          documentos: restantes,
          cantidadFilas: restantes.length,
          subtotalComision: restantes.reduce((acc, r) => acc + (Number(r.impComision) || 0), 0),
        };
      });
      setSelectedIds(new Set());
      setConcCom1('');
      setConcComImp1('');
      setConcCom2('');
      setConcComImp2('');

      setSuccessResult(result);
      setSuccessOpen(true);
    } catch (error) {
      enqueueSnackbar(getComisionErrorMessage(error, 'Error al confirmar la liquidación'), {
        variant: 'error',
      });
    } finally {
      setLiquidando(false);
    }
  };

  const irAlListado = () => {
    setSuccessOpen(false);
    setSuccessResult(null);
    navigate(LISTADO_LIQUIDACIONES);
  };

  const verDetalleFactura = () => {
    const nroFc = successResult?.nroFormularioComision;
    setSuccessOpen(false);
    setSuccessResult(null);
    if (nroFc && nroFc > 0) {
      navigate(`/compras/facturas/${nroFc}`);
      return;
    }
    navigate(LISTADO_LIQUIDACIONES);
  };

  const allSelected = rows.length > 0 && selectedIds.size === rows.length;
  const baseLabel = labelImporteBase(comisionBase);
  const puedeLiquidar =
    selectedIds.size > 0 && !liquidando && !loading && preview != null && configLiquidacionOk;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={1} sx={{ mb: 3 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              sx={{ gap: 1 }}
            >
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Nueva liquidación de comisiones
              </Typography>
              <Button
                variant="outlined"
                onClick={() => navigate(LISTADO_LIQUIDACIONES)}
                sx={{ textTransform: 'none' }}
              >
                Volver al listado
              </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Al confirmar se genera la Factura Interna X al proveedor y cuenta de comisión del
              vendedor.
            </Typography>
          </Stack>

          <Alert severity="info" sx={{ mb: 3 }}>
            El vendedor debe tener proveedor asociado y cuenta contable de comisión. La sucursal al
            liquidar es la del comprobante X (obligatoria; no usar “Todas”).
          </Alert>

          <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
            <Grid item xs={12} md={3}>
              <VendedorSelect
                label="Vendedor"
                value={nroVendedor}
                onChange={(nro) => setNroVendedor(nro)}
                allowEmpty
                emptyOptionLabel="Seleccionar…"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FiltroSucursalVentaSelect
                value={filtroNroSuc}
                onChange={setFiltroNroSuc}
                loading={loadingSucursales}
                sucursales={sucursales}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Fecha Desde"
                type="date"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Fecha Hasta"
                type="date"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                fullWidth
                startIcon={loading ? undefined : <SearchIcon />}
                onClick={() => void handleBuscar()}
                disabled={loading || liquidando}
                sx={{ textTransform: 'none', height: 56, borderRadius: 2, fontWeight: 500 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Buscar'}
              </Button>
            </Grid>
          </Grid>

          {preview && (
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
              {preview.nombreVendedor && (
                <Chip
                  size="small"
                  label={`Vendedor: ${preview.nombreVendedor}${
                    preview.comisionPorc != null ? ` (${preview.comisionPorc}%)` : ''
                  }`}
                />
              )}
              {preview.comisionBase && (
                <Chip size="small" label={`Base: ${preview.comisionBase}`} variant="outlined" />
              )}
              {tieneProveedor ? (
                <Chip
                  size="small"
                  label={`Prov. ${preview.nroProveedor}${
                    preview.nombreProveedor ? ` — ${preview.nombreProveedor}` : ''
                  }`}
                  variant="outlined"
                />
              ) : (
                <Chip size="small" color="warning" label="Sin proveedor asociado" />
              )}
              {tieneCuenta ? (
                <Chip
                  size="small"
                  label={`Cta. ${preview.nroCuentaLiquidacion}${
                    preview.nombreCuentaLiquidacion
                      ? ` — ${preview.nombreCuentaLiquidacion}`
                      : ''
                  }`}
                  variant="outlined"
                />
              ) : (
                <Chip size="small" color="warning" label="Sin cuenta de comisión" />
              )}
            </Stack>
          )}

          {preview && !configLiquidacionOk && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No se puede liquidar hasta completar proveedor y cuenta de comisión del vendedor en
              Ventas → Vendedores.
            </Alert>
          )}

          <TableContainer
            component={Paper}
            sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0', mb: 3, overflowX: 'auto' }}
          >
            <Table size="small" sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={selectedIds.size > 0 && !allSelected}
                      onChange={(_, checked) => toggleAll(checked)}
                      disabled={rows.length === 0 || loading || liquidando}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Comprobante</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Nro. interno
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    {baseLabel}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Comisión
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        Sin comprobantes. Completá filtros y buscá.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row: DocumentoLiquidableDto) => (
                    <TableRow key={row.nroFormulario} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.has(row.nroFormulario)}
                          onChange={() => toggleRow(row.nroFormulario)}
                          disabled={liquidando}
                        />
                      </TableCell>
                      <TableCell>{formatFechaDisplay(row.fechaEmision)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.formularioDisplay?.trim() || '—'}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {row.nombreCliente?.trim() ||
                          (row.nroCliente != null ? `#${row.nroCliente}` : '—')}
                      </TableCell>
                      <TableCell align="right">{row.nroFormulario}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(importeBaseDocumento(row, comisionBase))}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
                        {formatCurrency(row.impComision ?? 0)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Accordion
            disableGutters
            elevation={0}
            sx={{
              mb: 3,
              border: '1px solid #e0e0e0',
              borderRadius: '8px !important',
              '&:before': { display: 'none' },
              overflow: 'hidden',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: '#fafafa',
                minHeight: 52,
                '& .MuiAccordionSummary-content': {
                  my: 1,
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                },
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Conceptos adicionales
              </Typography>
              <Typography variant="body2" color="text.secondary">
                (opcionales)
              </Typography>
              {totalConceptos !== 0 && (
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`Ajustes: ${formatCurrency(totalConceptos)}`}
                />
              )}
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 1, pb: 2, px: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Concepto 1"
                    value={concCom1}
                    onChange={(e) => setConcCom1(e.target.value.slice(0, 50))}
                    fullWidth
                    inputProps={{ maxLength: 50 }}
                    helperText="Ajuste opcional (máx. 50 caracteres)"
                    disabled={liquidando}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Importe concepto 1"
                    value={concComImp1}
                    onChange={(e) => setConcComImp1(e.target.value)}
                    fullWidth
                    inputProps={{ inputMode: 'decimal' }}
                    disabled={liquidando}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'block' } }} />
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Concepto 2"
                    value={concCom2}
                    onChange={(e) => setConcCom2(e.target.value.slice(0, 50))}
                    fullWidth
                    inputProps={{ maxLength: 50 }}
                    helperText="Segundo ajuste opcional"
                    disabled={liquidando}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Importe concepto 2"
                    value={concComImp2}
                    onChange={(e) => setConcComImp2(e.target.value)}
                    fullWidth
                    inputProps={{ inputMode: 'decimal' }}
                    disabled={liquidando}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Total comisión a liquidar:{' '}
                <Box component="span" sx={{ color: '#1976d2' }}>
                  {formatCurrency(totalComision)}
                </Box>
              </Typography>
              {(selectedIds.size > 0 || totalConceptos !== 0) && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Docs: {formatCurrency(totalDocs)}
                  {totalConceptos !== 0 ? ` · Ajustes: ${formatCurrency(totalConceptos)}` : ''}
                  {selectedIds.size > 0
                    ? ` · ${selectedIds.size} comprobante${selectedIds.size === 1 ? '' : 's'}`
                    : ''}
                </Typography>
              )}
              {preview != null && selectedIds.size === rows.length && rows.length > 0 && totalConceptos === 0 && (
                <Typography variant="caption" color="text.secondary">
                  Subtotal API: {formatCurrency(preview.subtotalComision)} · {preview.cantidadFilas}{' '}
                  fila{preview.cantidadFilas === 1 ? '' : 's'}
                </Typography>
              )}
            </Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <TextField
                label="Fecha emisión liquidación"
                type="date"
                value={fechaEmision}
                onChange={(e) => setFechaEmision(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Fecha de la Factura X"
                sx={{ minWidth: 220 }}
              />
              <Can permiso="ventas.liquidacion-comisiones.nuevo">
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!puedeLiquidar}
                  onClick={handleAbrirConfirmacion}
                  sx={{
                    textTransform: 'none',
                    px: 3,
                    height: 56,
                    borderRadius: 2,
                    fontWeight: 500,
                    alignSelf: { sm: 'flex-start' },
                  }}
                >
                  {liquidando ? <CircularProgress size={24} color="inherit" /> : 'Confirmar liquidación'}
                </Button>
              </Can>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Dialog
        open={confirmOpen}
        onClose={() => !liquidando && setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1.5, pt: 3, px: 3 }}>
          Confirmar liquidación
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2, px: 3 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5 }}>
            Se generará una Factura Interna X al proveedor del vendedor. Los comprobantes
            seleccionados quedarán marcados como liquidados.
          </Typography>
          <Stack spacing={0.5}>
            <Typography variant="body2">
              <strong>Vendedor:</strong>{' '}
              {preview?.nombreVendedor?.trim() || (nroVendedor != null ? `#${nroVendedor}` : '—')}
            </Typography>
            <Typography variant="body2">
              <strong>Comprobantes:</strong> {selectedIds.size}
            </Typography>
            <Typography variant="body2">
              <strong>Fecha emisión:</strong> {formatFechaDisplay(fechaEmision)}
            </Typography>
            {totalConceptos !== 0 && (
              <Typography variant="body2">
                <strong>Ajustes:</strong> {formatCurrency(totalConceptos)}
              </Typography>
            )}
            <Typography variant="body2">
              <strong>Total:</strong> {formatCurrency(totalComision)}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={liquidando}
            sx={{ textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => void handleConfirmar()}
            disabled={liquidando}
            sx={{ textTransform: 'none' }}
          >
            {liquidando ? <CircularProgress size={22} color="inherit" /> : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={successOpen}
        onClose={irAlListado}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1.5, pt: 3, px: 3 }}>
          Liquidación registrada
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2, px: 3 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            {successResult?.mensaje?.trim() ||
              'La comisión se liquidó correctamente y se generó la Factura Interna X.'}
          </Alert>
          <Stack spacing={0.5}>
            {(successResult?.formularioDisplay?.trim() ||
              (successResult?.nroFormularioComision != null &&
                successResult.nroFormularioComision > 0)) && (
              <Typography variant="body2">
                <strong>Comprobante:</strong>{' '}
                {successResult?.formularioDisplay?.trim() ||
                  `#${successResult?.nroFormularioComision}`}
              </Typography>
            )}
            {successResult?.nombreProveedor?.trim() && (
              <Typography variant="body2">
                <strong>Proveedor:</strong> {successResult.nombreProveedor.trim()}
              </Typography>
            )}
            {successResult?.totalComision != null && (
              <Typography variant="body2">
                <strong>Total:</strong> {formatCurrency(successResult.totalComision)}
              </Typography>
            )}
            {successResult?.documentosLiquidados != null && successResult.documentosLiquidados > 0 && (
              <Typography variant="body2">
                <strong>Documentos liquidados:</strong> {successResult.documentosLiquidados}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button onClick={irAlListado} sx={{ textTransform: 'none' }}>
            Volver a liquidación
          </Button>
          <Button
            onClick={verDetalleFactura}
            variant="contained"
            color="primary"
            disabled={!(successResult?.nroFormularioComision && successResult.nroFormularioComision > 0)}
            sx={{ textTransform: 'none' }}
          >
            Ver detalle de la factura
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LiquidacionComisiones;
